import React, {
  useState,
  forwardRef,
  useRef,
  useImperativeHandle,
  useEffect,
} from "react";
import classNames from "classnames";

import ReactPlayer from "react-player";

import { useSpring, a, SpringConfig } from "@react-spring/web";
import { defaultSpringConfig } from "components/Animations/SpringProperties/SpringProperties";

import { useLatest } from "utils/Index";

interface IOwnSpecificProps {
  width?: string;
  height?: string;
  heightRatio?: number;
  url: string;
  placeholderSrc?: string;
  placeholderBackgroundColor?: string;
  playVideo?: boolean;
  loop?: boolean;
  autoPlay?: boolean;
  muted?: boolean;
  animateVideoIn: boolean;
  animationType: "reveal" | "fadeIn";
}

type IOwnProps = IOwnSpecificProps;

export type IProps = IOwnProps;

const LazyVideoComponent: React.FC<IProps> = ({
  height,
  width,
  heightRatio,
  url,
  placeholderSrc,
  placeholderBackgroundColor,
  playVideo,
  loop,
  autoPlay,
  muted,
  animationType,
  animateVideoIn,
  ...props
}) => {
  const [animationReady, setAnimationReady] = useState(false);

  const handleVideoLoad = () => {
    if (!animationReady) setAnimationReady(true);
  };

  const springConfig: SpringConfig = {
    ...defaultSpringConfig,
    tension: 135,
  };
  const [triggerPlay, setTriggerPlay] = useState(false);
  const latestTriggerPlay = useLatest(triggerPlay);

  const springOnChange = (result: any) => {
    // console.log('------------------------------------------------------------------------------------------------------------------------------------ frame function');
    // console.log(url, value, triggerPlay, latestTriggerPlay);
    if (result > 0.5 && !latestTriggerPlay.current) setTriggerPlay(true);
  };

  const [animateInSpring, setAnimateInSpring] = useSpring(() => ({
    x: 0,
    opacity: 0,
    play: 0,
    config: springConfig,
    onChange: {
      play: (result) => springOnChange(result),
    },
  }));

  useEffect(() => {
    //console.log('------------------------------------------------------------------------------------------------------------------------------------ effect');
    //console.log(url + ' ' + JSON.stringify(animationReady) + ' ' + JSON.stringify(animateVideoIn));
    setAnimateInSpring({
      x:
        animationReady && animateVideoIn && animationType === "reveal"
          ? 100
          : 0,
      opacity:
        animationReady && animateVideoIn && animationType === "fadeIn" ? 1 : 0,
      play: animationReady && animateVideoIn ? 1 : 0,
    });
  }, [animationReady, animateVideoIn, animationType]);

  const videoStyle = {
    height,
    width,
  };

  const aspectRatioSpacerStyles = {
    paddingBottom: heightRatio ? heightRatio * 100 + "%" : null,
  };

  const placeholderVideoStyles = {
    position:
      placeholderSrc && !heightRatio ? ("relative" as "relative") : null,
    opacity: placeholderSrc ? 1 : 0,
  };

  const sharedPlaceholderStyles = {
    backgroundColor: placeholderBackgroundColor
      ? placeholderBackgroundColor
      : "black",
  };

  const fadeInPlaceholderVideoStyles = {
    ...sharedPlaceholderStyles,
    opacity:
      animationType === "fadeIn"
        ? animateInSpring.opacity.to((x) => (1 - x) * 0.02)
        : 0,
  };

  const revealPlaceholderVideoStyles = {
    ...sharedPlaceholderStyles,
    transform: animateInSpring.x.to(
      (x) => `translateX(  calc(  ${x} * 1%  )  )`
    ),
    opacity: animationType === "reveal" ? 1 : 0,
  };

  const getVideo = () => {
    if (animateVideoIn)
      return (
        <ReactPlayer
          className="react-player"
          width={width}
          height={height}
          url={url}
          autoPlay={autoPlay}
          muted={muted}
          playing={playVideo && triggerPlay}
          loop={loop}
          onReady={handleVideoLoad}
        />
      );
    else return <div />;
  };

  return (
    <figure className="lazy-video" style={videoStyle}>
      <div className="videoWrapper">
        <div className="aspectRatioSpacer" style={aspectRatioSpacerStyles}>
          <img
            className={"placeholder"}
            src={placeholderSrc ? placeholderSrc : ""}
            style={placeholderVideoStyles}
          />
          <a.div
            className="videoPlaceholder"
            style={fadeInPlaceholderVideoStyles}
          />
          {getVideo()}
        </div>
      </div>
      <a.div
        className={classNames("videoPlaceholder", "reveal")}
        style={revealPlaceholderVideoStyles}
      />
    </figure>
  );
};

export const LazyVideo = LazyVideoComponent;
